/**
 * Price formatting utilities.
 * All prices on Easytiger are in VND — displayed in Vietnamese local
 * format (dots as thousand separators, đ suffix). Only raw digits are
 * stored in the database.
 */

/** Strip everything except digits from a price string. */
export function stripToDigits(value: string): string {
  return value.replace(/[^\d]/g, "");
}

/** Format a digit-only string with dot separators: "15000000" → "15.000.000" */
export function formatWithDots(digits: string): string {
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Format a raw price value for display in Vietnamese local format.
 * Strips any existing ₫/đ/From prefix, extracts digits, and returns "15.000.000đ".
 * Returns the original string if it contains no digits (e.g. "Contact for price").
 */
export function formatPrice(raw: string | null | undefined): string {
  if (!raw || !raw.trim()) return "";
  // A percentage fee ("1.5% of purchase price") is NOT an amount. It contains
  // digits, so a plain digit-strip would render it as "15đ" — a 1.5% commission
  // shown as fifteen dong. Show the professional's own wording instead.
  if (raw.includes("%")) return raw;
  const digits = stripToDigits(raw);
  if (!digits) return raw; // non-numeric price like "Contact for price"
  return `${formatWithDots(digits)}đ`;
}

/**
 * Format a price and report whether it's a real number, for the "from …"
 * prefix on service prices. `isNumeric` is false for empty / non-numeric
 * values (e.g. "Contact for price"), so callers can suppress the prefix.
 * The prefix markup + sizing stays at each call site (it varies by surface).
 */
export function priceParts(raw: string | null | undefined): {
  formatted: string;
  isNumeric: boolean;
  isFree: boolean;
} {
  const formatted = formatPrice(raw);
  // A percentage fee is shown verbatim and must NOT take the "from" prefix —
  // "from 1.5% of purchase price" reads as a bungled amount.
  const isPercent = typeof raw === "string" && raw.includes("%");
  return {
    formatted,
    isNumeric: !isPercent && /\d/.test(formatted),
    isFree: isFreePrice(raw),
  };
}

/**
 * A price of zero means the service is free — a professional offering a free
 * first consultation enters `0` rather than leaving the field blank (blank now
 * means "no price given", which the dashboard rejects). Callers render their
 * own localized "Free" label; showing the literal "from 0đ" reads as a bug.
 */
export function isFreePrice(raw: string | null | undefined): boolean {
  if (!raw || raw.includes("%")) return false; // a percentage is never "free"
  const digits = stripToDigits(raw);
  return digits !== "" && Number(digits) === 0;
}

/**
 * The headline price for a professional in search results: the LOWEST real
 * amount across their packages.
 *
 * Excluded, so the headline is never misleading:
 *   - **free packages (0)** — a free consultation would otherwise make every
 *     pro who offers one look like the cheapest in the directory;
 *   - **hidden prices** — they arrive as "" (the public column is NULL);
 *   - **percentage fees** ("1.5% of purchase price") — not an amount at all;
 *     a naive digit-strip would read it as 15₫ and win the minimum outright.
 *
 * Returns a digits-only string ready for `formatPrice`, or null when the pro
 * has no publishable amount — callers then show "Contact for price".
 */
/**
 * Every AMOUNT in a price string, lowest first.
 *
 * A digit-strip over the whole string is wrong the moment a professional
 * writes a RANGE: "5.000.000 - 10.000.000 VND" collapsed to the single number
 * 500000010000000 (review 2026-08-24). That was merely an ugly card until
 * budget became a HARD filter, at which point it silently excluded that
 * professional from every budgeted search — the opposite of the "unknown is
 * not too expensive" rule, and unrecoverable by the reader.
 *
 * Numbers are split on anything that is not a digit or an in-number separator,
 * so thousand separators inside one amount are preserved ("5.000.000" stays
 * one number) while a range yields two.
 */
export function priceAmounts(raw: string | null | undefined): number[] {
  if (!raw || raw.includes("%")) return [];
  const out: number[] = [];
  // A token is digits possibly broken by . or , as thousand separators. The
  // separators are then removed; a decimal comma/point inside a VND amount is
  // not a thing (the currency has no minor unit in practice).
  for (const m of raw.matchAll(/\d[\d.,]*/g)) {
    const n = Number(m[0].replace(/[.,]/g, ""));
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/**
 * The professional's cheapest PAID package, as a digit string, or null.
 *
 * "Paid" excludes 0 (free) and anything with a `%` (a commission is not an
 * amount). For a range, this is the BOTTOM of it — which is what "From ..."
 * means on the card and what a budget filter should compare against.
 */
export function lowestPaidPrice(raws: (string | null | undefined)[]): string | null {
  let min: number | null = null;
  for (const raw of raws) {
    for (const n of priceAmounts(raw)) {
      if (min === null || n < min) min = n;
    }
  }
  return min === null ? null : String(min);
}
